// EL LOGO DE BIANGO PINTANDOSE DE ABAJO ARRIBA MIENTRAS LLEGA LA MARCA DE LA CLINICA
//
// Lo pidio el dueño el 2-sep-2026 con estas palabras: «es mejor que haya el logo de Biango cargando
// asi como pintandose de abajo arriba mientras carga todo en segundo plano para mostrarlo
// correctamente».
//
// POR QUE EXISTE ESTO Y NO UN «Cargando…». El color de la clinica llega en una segunda respuesta
// (`/api/config-cliente`), asi que durante un instante la app tenia que enseñar ALGO: hasta hoy
// enseñaba la marca del DOMINIO —o sea, con la cuenta unica, la de OTRA clinica— y la cambiaba al
// llegar la buena. El parpadeo no era feo: era el nombre y el color de otra empresa encima de los
// datos de esta. Ahora, mientras no se sepa de quien es la pantalla, no se dice de nadie: se dice
// del PRODUCTO, que no es de ningun cliente.
//
// El relleno de abajo arriba es una mascara sobre el mismo texto, no una segunda copia desalineada:
// dos capas identicas, la de arriba recortada por `clip-path` que sube. Con `prefers-reduced-motion`
// se queda quieto y lleno — quien ha pedido que nada se mueva no tiene por que ver esto latir.
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { t as i18nT, marca, tDato } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// ── Y MIENTRAS, SE VA CARGANDO LO DE HOY ─────────────────────────────────────────────────────
//  Lo pidio el dueño el 2-sep: «ya mientras en segundo plano carga el color de esa clinica y todo
//  lo que corresponda para que al entrar las 4 cosas basicas de recepcion, agenda, inicio y cosas
//  asi ya esten cargadas en el dia de hoy».
//
//  Se piden con LAS MISMAS CLAVES que usan esas pantallas, no con una copia: si la clave no casara,
//  la pantalla volveria a pedirlo todo al abrirse y esto seria trabajo tirado (y una peticion de mas
//  por cada entrada). Todas fallan en silencio a proposito: esto es un adelanto, y si algo no se
//  puede traer ahora, la pantalla lo pedira ella cuando toque — nunca se bloquea la entrada por un
//  adelanto que no ha salido.
const HOY = () => {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate())
}
export function useAdelantarLoDeHoy() {
  const qc = useQueryClient()
  // `listo` = ya han contestado (bien o mal) las peticiones del dia. Es lo que decide cuanto se
  // queda esta pantalla: ver el logo mientras cargan los datos es lo que el dueño pidio —«que ya
  // salga todo en pantalla correctamente en vez de tener que esperar ellos viendo solo parte de los
  // datos o sin datos directamente»—.
  const [listo, setListo] = useState(false)
  useEffect(() => {
    const hoy = HOY()
    const pedir = (queryKey, url) => (
      // `fetchQuery` y no `prefetchQuery`: el segundo se salta la peticion si ya hay algo en cache,
      // y aqui la cache acaba de vaciarse al entrar (main.jsx la limpia con la sesion).
      qc.fetchQuery({ queryKey, queryFn: () => api.get(url).then(r => r.data), staleTime: 20000 })
        .catch(() => {})
    )
    // Recepcion (Dashboard) y Agenda comparten el endpoint del dia pero NO la clave: cada pantalla
    // guarda la suya, asi que se adelantan las dos. Es la misma respuesta del servidor.
    // `allSettled`: si una falla, se sigue igual. Esto es un adelanto, no una condicion para entrar.
    let vivo = true
    Promise.allSettled([
      pedir(['citasHoy', hoy], '/citas/hoy'),
      pedir(['citas', hoy, false], '/citas/hoy?fecha=' + hoy + '&incluirCanceladas=true'),
    ]).then(() => { if (vivo) setListo(true) })
    // ── Y UN TECHO, QUE ES LA MITAD QUE IMPORTA ────────────────────────────────────────────────
    //  Esperar a los datos esta bien; esperar SIN LIMITE es dejar a la clinica fuera de su programa
    //  el dia que el servidor va lento o no contesta. A los 6 segundos se entra igual: la pantalla
    //  pedira lo que le falte por su cuenta, como ha hecho siempre. Mas vale entrar con la agenda a
    //  medio pintar que no poder entrar.
    const techo = setTimeout(() => { if (vivo) setListo(true) }, 6000)
    return () => { vivo = false; clearTimeout(techo) }
  }, [qc])
  return listo
}

// ── «CARGANDO ___», CON LA PALABRA GIRANDO ───────────────────────────────────────────────────
//
//  Lo pidio el dueño asi: «la palabra cargando se queda fija siempre en el mismo sitio y la palabra
//  citas, datos, pacientes y otras palabras son las que se deslizan hacia abajo desapareciendo, y la
//  nueva aparece desde arriba».
//
//  LAS PALABRAS DICEN LA VERDAD, que es lo unico que me he permitido decidir: mientras esta pantalla
//  esta puesta se piden las CITAS DE HOY y la MARCA de la clinica, y eso es lo que se nombra. Poner
//  «cargando pacientes» cuando no se esta cargando ningun paciente es una mentira pequeña, pero es
//  una mentira — y esta pantalla la ve el equipo cada mañana.
//
//  El giro es una ventana de UNA linea con `overflow:hidden` y dos capas: la que se va baja y se
//  desvanece, la que entra viene de arriba. Nada de dos copias desalineadas: las dos ocupan la misma
//  celda. Con `prefers-reduced-motion` no se mueve nada y se queda una palabra quieta — quien ha
//  pedido que nada se mueva no tiene por que ver esto girar.
// Cortas A PROPOSITO: en mayusculas y con espaciado, una frase larga no cabe en la ventana y se
// cortaria a media letra. Y siguen nombrando lo que de verdad se esta pidiendo (las citas de hoy y
// la marca de la clinica), que es la regla que sujeta su guardia.
const PALABRAS = [marca('components.CargandoMarca.las_citas_de_hoy', 'las citas de hoy'), marca('components.CargandoMarca.la_agenda', 'la agenda'), marca('components.CargandoMarca.tu_clinica', 'tu clínica'), marca('components.CargandoMarca.lo_de_hoy', 'lo de hoy')]
const CADA_PALABRA = 1400

function PalabraQueGira({ quieto }) {
  const ALTO = 22;
  const [i, setI] = useState(0)
  const [bajando, setBajando] = useState(false)
  useEffect(() => {
    if (quieto) return undefined
    const t = setInterval(() => {
      setBajando(true)
      // Cuando la caida ha terminado: se avanza la palabra y se vuelve arriba SIN transicion (el
      // salto no se ve porque la de arriba es justo la que se acaba de dejar en la ventana).
      setTimeout(() => { setI((n) => (n + 1) % PALABRAS.length); setBajando(false) }, 320)
    }, CADA_PALABRA)
    return () => clearInterval(t)
  }, [quieto])

  // LA CINTA lleva DOS palabras: arriba la que va a entrar, debajo la que se ve. En reposo la cinta
  // esta subida (-ALTO) y se ve la de abajo; al girar baja a 0, con lo que la de abajo se va por el
  // suelo y la de arriba ocupa su sitio. Es una sola pieza moviendose: asi no hay dos elementos que
  // puedan quedar desalineados ni un parpadeo entre uno y otro.
  const cinta = {
    display: 'block',
    transform: 'translateY(' + (bajando ? 0 : -ALTO) + 'px)',
    transition: (quieto || !bajando) ? 'none' : 'transform 300ms cubic-bezier(.4,0,.2,1)',
  }
  // MAYUSCULAS Y MAS AIRE ENTRE LETRAS: en un movil, a contraluz y de pasada, el texto pequeño en
  // minusculas de un gris claro no se lee. Va centrado DENTRO de su ventana para que el conjunto
  // «CARGANDO ___» quede simetrico respecto al logo: con el texto pegado a la izquierda de una
  // ventana ancha, la tinta se veia corrida hacia un lado aunque el bloque estuviera centrado.
  const palabra = {
    display: 'block', height: ALTO, lineHeight: ALTO + 'px', whiteSpace: 'nowrap',
    fontSize: '14px', fontWeight: 800, letterSpacing: '.6px', textTransform: 'uppercase',
    color: 'var(--primary, #1E88E5)', textAlign: 'center',
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px' }}>
      <span style={{
        fontSize: '13px', fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase',
        color: 'var(--text-secondary, #6b7280)',
      }}>{i18nT('components.CargandoMarca.cargando', 'Cargando')}</span>
      {/* La ventana: alto de UNA linea, y lo que sobra por arriba y por abajo no se ve. El ancho
          minimo evita que el conjunto de saltos al pasar de una palabra corta a una larga. */}
      {/* Ancho FIJO al de la palabra mas larga: si la ventana creciera y encogiera con cada palabra,
          el conjunto daria un salto lateral en cada giro y eso se ve como un temblor. */}
      <span style={{ position: 'relative', display: 'inline-block', height: ALTO, width: 178, overflow: 'hidden' }}>
        <span style={cinta}>
          <span style={palabra} aria-hidden="true">{tDato(PALABRAS[(i + 1) % PALABRAS.length])}</span>
          {/* La que se esta leyendo: es la unica que se anuncia a un lector de pantalla. */}
          <span style={palabra} aria-live="polite">{tDato(PALABRAS[i])}</span>
        </span>
      </span>
    </div>
  )
}

export default function CargandoMarca({ texto = 'Biango' }) {
  const [pct, setPct] = useState(0)
  const quieto = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false

  useEffect(() => {
    if (quieto) { setPct(100); return undefined }
    // Sube y vuelve a empezar. No es una barra de progreso: NO sabemos cuanto falta, y fingir un
    // porcentaje real seria mentir. Es un latido que dice «esto sigue vivo».
    let v = 0
    const t = setInterval(() => { v = (v + 4) % 130; setPct(Math.min(100, v)) }, 40)
    return () => clearInterval(t)
  }, [quieto])

  const base = {
    fontSize: 'clamp(28px, (7vw / var(--z, 1)), 44px)', fontWeight: 800, letterSpacing: '-1px',
    lineHeight: 1.1, userSelect: 'none',
  }

  return (
    // EN EL CENTRO DE LA PANTALLA, no a un tercio. Ocupaba el 60% del alto y el conjunto quedaba
    // alto; en un movil, con el teclado o la barra del navegador, quedaba aun mas arriba. `100dvh`
    // (no `100vh`) porque en iOS el `vh` cuenta la barra que se esconde y deja el contenido corrido.
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: 'var(--alto-pantalla)', gap: '18px', padding: '24px', textAlign: 'center',
    }}>
      <div style={{ position: 'relative' }} aria-label={i18nT('components.CargandoMarca.cargando', 'Cargando')} role="status">
        {/* La capa de abajo: el contorno apagado, para que la palabra se lea desde el primer frame. */}
        <div style={{ ...base, color: 'var(--text-muted, #9ca3af)', opacity: 0.35 }}>{texto}</div>
        {/* La de arriba, recortada: sube de abajo arriba. `inset` recorta por los cuatro lados, y el
            de arriba es el que se mueve — 100% recortado (invisible) a 0% (entera). */}
        <div aria-hidden="true" style={{
          ...base, position: 'absolute', inset: 0, color: 'var(--primary, #2563eb)',
          clipPath: `inset(${100 - pct}% 0 0 0)`,
          transition: quieto ? 'none' : 'clip-path 40ms linear',
        }}>{texto}</div>
      </div>
      <PalabraQueGira quieto={quieto} />
    </div>
  )
}
